<template>
  <div class="modal-content idml-preview">
    <div class="modal-header">
      <h5
        id="idml-preview-title"
        class="modal-title"
      >
        IDML Preview<span v-if="currentFile">: {{ currentFile.name }}</span>
      </h5>
      <button
        type="button"
        class="close"
        data-dismiss="modal"
        aria-label="Close"
        @click="cancelImport"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
    <div class="modal-body">
      <div
        v-if="files.length > 1"
        class="mb-3"
      >
        <label for="idml-preview-file">File</label>
        <select
          id="idml-preview-file"
          v-model.number="currentFileIndex"
          class="form-control"
          @change="resetSelection"
        >
          <option
            v-for="(file, index) in files"
            :key="file.url"
            :value="index"
          >
            {{ file.name }}
          </option>
        </select>
      </div>
      <div
        v-if="currentFile && currentFile.isFetching"
        class="text-center py-5"
        role="status"
      >
        <span class="spinner-border text-primary" />
        <p class="mt-3">
          Parsing IDML…
        </p>
      </div>
      <div
        v-else-if="currentFile && currentFile.error"
        class="alert alert-danger"
        role="alert"
      >
        {{ currentFile.error }}
        <button
          type="button"
          class="btn btn-outline-danger btn-sm ml-2"
          @click="loadFile(currentFile, true)"
        >
          Retry
        </button>
      </div>
      <template v-else-if="currentFile && currentFile.data">
        <div
          v-if="currentFile.data.requiredImages.length"
          class="alert alert-warning"
        >
          {{ currentFile.data.requiredImages.length }} image(s) need to be uploaded to be imported.
          <div class="mt-2">
            <button
              type="button"
              class="btn btn-outline-dark btn-sm"
              :disabled="currentFile.isResolving"
              @click="$refs.linksFolderInput.click()"
            >
              Upload Links folder
            </button>
            <input
              ref="linksFolderInput"
              type="file"
              multiple
              webkitdirectory
              directory
              class="d-none"
              @change="uploadLinksFolder"
            >
          </div>
          <details
            class="mt-2"
            open
          >
            <summary>Required images</summary>
            <ul class="mb-0">
              <li
                v-for="(image, index) in currentFile.data.requiredImages"
                :key="index"
                class="idml-preview__wrap"
              >
                {{ image.resourceUri || image.imageId || image.layerId || 'Unnamed image' }}
                <span v-if="image.status === 'ambiguous'"> — multiple matching files</span>
                <button
                  type="button"
                  class="btn btn-link btn-sm"
                  :disabled="currentFile.isResolving"
                  @click="chooseReplacement(index)"
                >
                  Replace
                </button>
              </li>
            </ul>
          </details>
          <div
            v-if="currentFile.isResolving"
            class="small mt-2"
            role="status"
          >
            Saving images…
          </div>
        </div>
        <input
          ref="replacementInput"
          type="file"
          accept="image/*,.pdf,.psd,.eps"
          class="d-none"
          @change="uploadReplacement"
        >
        <div
          v-if="uploadMessage"
          class="alert"
          :class="uploadError ? 'alert-danger' : 'alert-info'"
          role="status"
        >
          {{ uploadMessage }}
        </div>
        <div class="d-flex align-items-center mb-3">
          <label
            for="idml-preview-page"
            class="mr-2 mb-0"
          >Page</label>
          <select
            id="idml-preview-page"
            v-model.number="pageIndex"
            class="form-control w-auto"
            :disabled="!pages.length"
            @change="selectedLayer = null"
          >
            <option
              v-for="(page, index) in pages"
              :key="page.properties.id || index"
              :value="index"
            >
              {{ page.name }} ({{ dimension(page.width) }} × {{ dimension(page.height) }} pt)
            </option>
          </select>
        </div>
        <div
          v-if="currentPage"
          class="row"
        >
          <div class="col-md-6 mb-3">
            <IDMLPagePreview
              :page="currentPage"
              :preview-path="currentPreviewPath"
              :selected="selectedLayer"
              @select="selectedLayer = $event"
            />
          </div>
          <div class="col-md-6">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h6 class="mb-0">
                Layers
              </h6>
              <div v-if="isImportFlow">
                <button
                  type="button"
                  class="btn btn-link btn-sm"
                  @click="setPageSelection(true)"
                >
                  Select all
                </button>
                <button
                  type="button"
                  class="btn btn-link btn-sm"
                  @click="setPageSelection(false)"
                >
                  Clear
                </button>
              </div>
            </div>
            <div class="idml-preview__layers">
              <IDMLLayersTree
                :nodes="currentPage.children || []"
                :selected="selectedLayer"
                :selectable="isImportFlow"
                :selected-ids="selectedIds"
                @select="selectedLayer = $event"
                @toggle="toggleLayer"
                @field-type="setFieldType"
              />
              <p v-if="!currentPage.children || !currentPage.children.length">
                No layers on this page.
              </p>
            </div>
          </div>
        </div>
        <p v-else>
          No pages found in this file.
        </p>
        <details
          v-if="unassignedLayers.length"
          class="mt-3"
        >
          <summary>Items outside pages ({{ unassignedLayers.length }})</summary>
          <p
            v-if="isImportFlow"
            class="small text-muted mb-1"
          >
            Items outside pages are shown for reference and are not imported.
          </p>
          <IDMLLayersTree
            :nodes="unassignedLayers"
            :selected="selectedLayer"
            @select="selectedLayer = $event"
          />
        </details>
        <div
          v-if="selectedLayer"
          class="border rounded p-3 mt-3 idml-preview__wrap"
        >
          <strong>{{ selectedLayer.name }}</strong>
          <div class="small text-muted">
            {{ selectedLayer.properties.kind }} · {{ dimension(selectedLayer.width) }} × {{ dimension(selectedLayer.height) }} pt
            · X: {{ dimension(selectedLayer.left) }}, Y: {{ dimension(selectedLayer.top) }}
          </div>
          <p
            v-if="selectedLayer.properties.text"
            class="idml-preview__text mt-2 mb-0"
          >
            {{ textContent(selectedLayer.properties.text) }}
          </p>
          <div
            v-if="selectedLayer.properties.style"
            class="small mt-2"
          >
            <div>Fill: {{ paintDescription(selectedLayer.properties.style.fill) }}</div>
            <div>Stroke: {{ paintDescription(selectedLayer.properties.style.stroke) }}</div>
          </div>
          <template v-if="selectedLayer.properties.image">
            <p class="mt-2 mb-0">
              {{ selectedLayer.properties.image.requiresUpload ? 'Image required' : 'Image saved' }}
            </p>
            <div>{{ selectedLayer.properties.image.resourceUri }}</div>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm mt-2"
              :disabled="currentFile.isResolving"
              @click="chooseLayerReplacement(selectedLayer)"
            >
              Replace image
            </button>
            <img
              v-if="selectedImageUrl"
              :key="selectedImageUrl"
              :src="selectedImageUrl"
              :alt="selectedLayer.name"
              class="idml-preview__image mt-2"
              @error="failedImageUrl = selectedImageUrl"
            >
          </template>
        </div>
      </template>
    </div>
    <div class="modal-footer">
      <span
        v-if="isImportFlow"
        class="mr-auto"
      >
        {{ selectedPagesCount }} page(s), {{ selectedLayersCount }} layer(s) selected
      </span>
      <button
        v-if="isImportFlow"
        type="button"
        class="btn btn-primary"
        :disabled="isImporting || !selectedLayersCount || hasSelectedMissingImages"
        @click="importTemplates"
      >
        <span
          v-if="isImporting"
          class="spinner-border spinner-border-sm mr-1"
        />
        Import selected layers
      </button>
      <button
        type="button"
        class="btn btn-outline-primary"
        :disabled="!currentFile || currentFile.isFetching"
        @click="loadFile(currentFile, true)"
      >
        Reparse IDML
      </button>
      <button
        type="button"
        class="btn btn-secondary"
        data-dismiss="modal"
        @click="cancelImport"
      >
        Close
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {
  IDML_LAYERS_EVENTS,
  IDML_LAYERS_FLOWS,
  idmlLayersEventBroker
} from '@frontend/group/modules/idml-layers/event-broker';
import IDMLLayersTree from './IDMLLayersTree.vue';
import IDMLPagePreview from './IDMLPagePreview.vue';
import LayoutService from '@frontend/services/api/layout';
import Route from '@frontend/services/helpers/url';
import {getUpdatedDataLayoutTemplatesGrid} from '@frontend/group/modules/get-updated-data-layout-templates-grid';
import {getLayoutInfo} from "@frontend/group/helpers/show/get-layout-info";

export default {
  name: 'IDMLPreview',
  components: {
    IDMLLayersTree,
    IDMLPagePreview
  },
  data() {
    return {
      files: [],
      currentFileIndex: 0,
      pageIndex: 0,
      selectedLayer: null,
      generation: 0,
      failedImageUrl: null,
      replacementTarget: null,
      uploadMessage: '',
      uploadError: false,
      flow: IDML_LAYERS_FLOWS.PREVIEW,
      cleanupAction: null,
      selectedIds: {},
      isImporting: false,
    };
  },
  computed: {
    currentFile() {
      return this.files[this.currentFileIndex];
      },
    pages() {
      return this.currentFile && this.currentFile.data ? this.currentFile.data.tree : [];
      },
    currentPage() {
      return this.pages[this.pageIndex];
      },
    currentPreviewPath() {
      const previews = this.currentFile && this.currentFile.data && this.currentFile.data.pagePreviews;
      return previews && previews[this.pageIndex] ? previews[this.pageIndex].storedPath : null;
    },
    selectedImageUrl() {
      const image = this.selectedLayer && this.selectedLayer.properties.image;
      if (!image || !image.storedPath || !/\.(png|jpe?g|gif|webp|avif|bmp)$/i.test(image.storedPath)) return null;
      const url = image.storedPath.startsWith('imports/')
        ? `/${image.storedPath.split('/').map(encodeURIComponent).join('/')}`
        : `/share?file=${encodeURIComponent(image.storedPath)}`;
      return url === this.failedImageUrl ? null : url;
    },
    unassignedLayers() {
      const data = this.currentFile && this.currentFile.data;
      return data ? data.unassignedLayers || [] : [];
    },
    isImportFlow() { return this.flow === IDML_LAYERS_FLOWS.CREATE_TEMPLATES; },
    selectedLayersCount() { return Object.values(this.selectedIds).filter(Boolean).length; },
    selectedPagesCount() {
      return this.files.reduce((count, file) => count + ((file.data && file.data.tree) || [])
        .filter(page => this.collectSelectedLayers(page.children || []).length).length, 0);
    },
    hasSelectedMissingImages() {
      return this.files.some(file => ((file.data && file.data.tree) || []).some(page =>
        this.collectSelectedLayers(page.children || []).some(layer =>
          layer.properties.kind === 'image' && layer.properties.image.requiresUpload
        )
      ));
    },
  },
  mounted() {
    idmlLayersEventBroker.on(IDML_LAYERS_EVENTS.INIT, this.init, true);
  },
  beforeDestroy() {
    this.generation++;
    idmlLayersEventBroker.off(IDML_LAYERS_EVENTS.INIT, this.init);
  },
  methods: {
    async init({files, isReparse = false, flow = IDML_LAYERS_FLOWS.PREVIEW, cleanupAction = null}) {
      const generation = ++this.generation;
      this.files = (Array.isArray(files) ? files : [files]).filter(Boolean).map(file => ({
        id: file.id,
        url: file.url,
        name: /\.idml$/i.test(file.name) ? file.name : `${file.name}.idml`,
        data: null,
        isFetching: true,
        isResolving: false,
        error: null,
      }));
      this.currentFileIndex = 0;
      this.flow = flow;
      this.cleanupAction = cleanupAction;
      this.selectedIds = {};
      this.resetSelection();
      for (const file of this.files) {
        if (generation !== this.generation) return;
        await this.loadFile(file, isReparse, generation);
      }
    },
    async loadFile(file, isReparse = false, generation = this.generation) {
      if (!file) return;
      file.isFetching = true;
      file.error = null;
      if (file === this.currentFile) this.resetSelection();
      try {
        const {data} = await axios.post('/file/preview_idml_file', {
          filename: file.url,
          originFileName: file.name,
          isReparse,
        });
        if (generation !== this.generation) return;
        file.data = {...data, requiredImages: data.requiredImages || []};
        this.prepareImportTree(file.data.tree || [], `${file.id || file.url}`);
        idmlLayersEventBroker.fire(IDML_LAYERS_EVENTS.IDML_FILE_PARSED, {
          fileId: file.id,
          thumbnailPath: data.fileThumbnailPath || null,
        });
      } catch (error) {
        if (generation !== this.generation) return;
        file.error = 'Could not parse this IDML file. Please retry.';
      } finally {
        if (generation === this.generation) file.isFetching = false;
      }
    },
    resetSelection() {
      this.pageIndex = 0;
      this.selectedLayer = null;
      this.failedImageUrl = null;
      this.replacementTarget = null;
      this.uploadMessage = '';
      this.uploadError = false;
    },
    prepareImportTree(nodes, prefix) {
      nodes.forEach((node, index) => {
        const id = `${prefix}:${node.properties.id || index}`;
        this.$set(node, '__importId', id);
        if (!node.__fieldType) {
          const fieldType = {
            text: 'Multi Line Text',
            image: 'Background Theme Image',
            shape: 'Rectangle',
          }[node.properties.kind];
          this.$set(node, '__fieldType', fieldType || null);
        }
        if (this.isImportFlow && this.isImportable(node) && node.visible !== false) {
          this.$set(this.selectedIds, id, true);
        }
        this.prepareImportTree(node.children || [], id);
      });
    },
    isImportable(node) {
      return ['text', 'image', 'shape'].includes(node.properties.kind)
        && (node.properties.kind !== 'image' || (
          !node.properties.image.requiresUpload
          && /\.(png|jpe?g|gif|webp|avif|bmp|tiff?)$/i.test(node.properties.image.storedPath || '')
        ));
    },
    walkLayers(nodes, callback) {
      nodes.forEach(node => {
        callback(node);
        this.walkLayers(node.children || [], callback);
      });
    },
    toggleLayer({node, selected}) {
      this.walkLayers([node], layer => {
        if (this.isImportable(layer)) this.$set(this.selectedIds, layer.__importId, selected);
      });
    },
    setPageSelection(selected) {
      if (!this.currentPage) return;
      this.walkLayers(this.currentPage.children || [], layer => {
        if (this.isImportable(layer)) this.$set(this.selectedIds, layer.__importId, selected);
      });
    },
    setFieldType({node, fieldType}) {
      this.$set(node, '__fieldType', fieldType);
    },
    collectSelectedLayers(nodes) {
      const layers = [];
      this.walkLayers(nodes, node => {
        if (this.selectedIds[node.__importId] && this.isImportable(node)) layers.push(node);
      });
      return layers;
    },
    getGridData(templates) {
      const oldTemplates = (window.previews || []).map(({template, settings}) => ({
        id: template.id,
        instanceId: settings.instance_id,
        name: template.name,
        width: template.width,
        height: template.height,
        content: $('<div></div>'),
        gridData: {x: settings.x, y: settings.y, w: settings.w, h: settings.h},
      }));
      const {oldTemplates: updatedTemplates, newTemplates} = getUpdatedDataLayoutTemplatesGrid(
        oldTemplates,
        templates.map(({width, height}) => ({width, height}))
      );
      const oldGridData = updatedTemplates.map(({instance_id, w, h, y, x}) => ({instance_id, w, h, y, x}));

      return newTemplates.map(({w, h, y, x}) => ({
        oldTemplates: oldGridData,
        newTemplate: {w, h, y, x},
      }));
    },
    async importTemplates() {
      if (this.isImporting) return;
      const {id, customerId} = getLayoutInfo();
      const templateSettings = [];
      this.isImporting = true;
      this.uploadMessage = '';

      try {
        const imports = this.files.map(file => ({
          file,
          templates: ((file.data && file.data.tree) || []).map(page => ({
            page,
            layers: this.collectSelectedLayers(page.children || []),
          })).filter(({page, layers}) => page.properties.id && layers.length)
            .map(({page, layers}) => ({
              pageId: page.properties.id,
              name: `${file.name.replace(/\.idml$/i, '')} ${page.name}`.trim(),
              layerIds: layers.map(layer => layer.properties.id).filter(Boolean),
              width: page.width,
              height: page.height,
            })).filter(template => template.layerIds.length),
        })).filter(({templates}) => templates.length);
        const gridData = this.getGridData(imports.flatMap(({templates}) => templates));
        let gridIndex = 0;

        for (const {file, templates} of imports) {
          const requestTemplates = templates.map(({width, height, ...template}) => ({
            ...template,
            gridData: gridData[gridIndex++],
          }));
          const {data} = await axios.post('/file/import_idml_templates', {
            filename: file.url,
            customerId,
            layoutId: id,
            templates: requestTemplates,
          });
          templateSettings.push(...data.templateSettings);
        }
        await LayoutService.updateLayoutSettings(customerId, id, templateSettings);
        if (this.cleanupAction) {
          localStorage.removeItem(this.cleanupAction);
          Route.deleteParam('action');
        }
        Route.reload();
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message;
        this.uploadMessage = message || 'Could not import the selected IDML layers.';
        this.uploadError = true;
      } finally {
        this.isImporting = false;
      }
    },
    cancelImport() {
      if (!this.cleanupAction) return;
      localStorage.removeItem(this.cleanupAction);
      Route.deleteParam('action');
      this.cleanupAction = null;
    },
    chooseReplacement(index) {
      this.replacementTarget = {index};
      this.$refs.replacementInput.click();
    },
    chooseLayerReplacement(layer) {
      this.replacementTarget = {
        layerId: layer.properties.id,
        imageId: layer.properties.image.id,
      };
      this.$refs.replacementInput.click();
    },
    async uploadReplacement(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (files.length !== 1 || !this.replacementTarget) return;
      const target = this.replacementTarget;
      this.replacementTarget = null;
      await this.resolveImages(files, target);
    },
    async uploadLinksFolder(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (files.length) await this.resolveImages(files);
    },
    async resolveImages(files, target = null) {
      const file = this.currentFile;
      if (!file || file.isResolving) return;
      const generation = this.generation;
      const form = new FormData();
      form.append('filename', file.url);
      files.forEach(upload => {
        form.append('files[]', upload);
        form.append('relativePaths[]', upload.webkitRelativePath || upload.name);
      });
      if (target && target.index !== undefined) {
        const image = file.data.requiredImages[target.index];
        form.append('targetIndex', target.index);
        if (image.layerId !== null) form.append('layerId', image.layerId);
        if (image.imageId !== null) form.append('imageId', image.imageId);
      } else if (target) {
        form.append('replaceExisting', '1');
        if (target.layerId != null) form.append('layerId', target.layerId);
        if (target.imageId != null) form.append('imageId', target.imageId);
      }

      file.isResolving = true;
      this.uploadMessage = '';
      try {
        const {data} = await axios.post('/file/resolve_idml_images', form);
        if (generation !== this.generation) return;
        file.data = data.data;
        this.prepareImportTree(file.data.tree || [], `${file.id || file.url}`);
        this.selectedLayer = null;
        this.uploadMessage = data.matched
          ? `${data.matched} image(s) saved. ${data.data.requiredImages.length} still required.`
          : 'No unique matches found. Replace the remaining images individually.';
        this.uploadError = false;
      } catch (error) {
        if (generation !== this.generation) return;
        const errors = error.response && error.response.data && error.response.data.errors;
        this.uploadMessage = errors ? Object.values(errors)[0][0] : 'Could not save the selected images.';
        this.uploadError = true;
      } finally {
        file.isResolving = false;
      }
    },
    dimension(value) { return Math.round((Number(value) || 0) * 100) / 100; },
    paintDescription(paint) {
      if (!paint || paint.type === 'none') return 'None';
      if (paint.type === 'gradient') return `${paint.name} (gradient)`;
      return `${paint.name || 'Color'}${paint.value ? ` (${paint.value})` : ''}`;
    },
    textContent(story) {
      const paragraphs = story.paragraphs || [];
      const tables = story.tables || [];
      const lines = [];

      paragraphs.forEach((paragraph, index) => {
        const text = (paragraph.runs || []).map(run => run.text || '').join('').trim();
        if (text) lines.push(text);

        tables.filter(table => table.afterParagraph === index).forEach(table => {
          const rows = {};
          (table.cells || []).forEach(cell => {
            const cellText = (cell.paragraphs || []).map(item =>
              (item.runs || []).map(run => run.text || '').join('')
            ).join('\n').trim();
            if (!rows[cell.row]) rows[cell.row] = {};
            rows[cell.row][cell.column] = cellText;
          });
          Object.keys(rows).sort((a, b) => Number(a) - Number(b)).forEach(row => {
            const columns = rows[row];
            const lastColumn = Math.max(...Object.keys(columns).map(Number));
            const values = Array.from({length: lastColumn + 1}, (_, column) => columns[column] || '');
            const text = values.join('\t').trimEnd();
            if (text) lines.push(text);
          });
        });
      });

      return lines.join('\n') || story.content || '';
    },
  },
};
</script>

<style scoped>
.idml-preview__layers {
  max-height: 65vh;
  overflow: auto;
}

.idml-preview__text {
  white-space: pre-wrap;
  max-height: 200px;
  overflow: auto;
}

.idml-preview__wrap,
.modal-title {
  overflow-wrap: anywhere;
}

.idml-preview__image {
  max-width: 100%;
  max-height: 240px;
  object-fit: contain;
}
</style>
